import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiClient } from "@/shared/lib/api-client";
import { SENDS_TEXTS } from "@/shared/lib/send-texts";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";
import { ensureSession } from "@/shared/lib/ensure-session";
import { useFunnelStore } from "@/store/funnel";
import {
  refreshChatSession,
  sessionQueryOptions,
} from "@/features/chat/api/use-session";

/** A dev build against a real backend: no codes go out and any code opens a
 *  case (see SENDS_TEXTS). Production builds never take this path. */
const DEV_NO_SMS = !SENDS_TEXTS;

/** What verifying tells us: who they turned out to be, and where they stand
 *  (missions filed; the one accepted and not yet reported, if any). */
export interface SignedIn {
  memberId: number;
  claimed: boolean;
  sessionId?: string;
  filed?: number;
  current?: {
    missionId: number;
    name: string;
    level: string;
    acceptedAt: number | null;
  } | null;
}

/** Pick up the mission they already have open on this browser, its clock
 *  where it was (the caller then goes to the live mission). */
export function takeUpMission(m: NonNullable<SignedIn["current"]>) {
  const lv = m.level.toLowerCase();
  useFunnelStore.setState({
    tier: lv.includes("improbable")
      ? "improbable"
      : lv.includes("challeng")
        ? "challenging"
        : "beginner",
    missionId: m.missionId,
    acceptedAt: m.acceptedAt ?? Date.now(),
    requested: true,
  });
}

export function digitsOnly(raw: string): string {
  let n = raw.replace(/\D/g, "");
  if (n.length === 11 && n.startsWith("1")) n = n.slice(1);
  return n;
}

/**
 * Phone login by texted code (Secret Missions' own endpoints): send a code to
 * a number, then verify it, which binds the session to that member. Shared by
 * the login modal and the debrief, where verifying is what files the report.
 */
export function usePhoneLogin({
  onSignedIn,
  successMessage = "You're in.",
  showCodeSentToast = true,
  signInOnly = false,
}: {
  onSignedIn?: (who?: SignedIn) => void;
  /** Only sign in to a number we already know: an unknown one is not kept on
   *  this session (the entry gate — a number alone must not let anyone in). */
  signInOnly?: boolean;
  /** "" for none: the caller says what happens next itself */
  successMessage?: string;
  showCodeSentToast?: boolean;
} = {}) {
  const queryClient = useQueryClient();
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [attemptId, setAttemptId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  // Closing/changing an attempt invalidates its pending responses. A late
  // verification must never reopen a dismissed case or advance a new pick.
  const generation = useRef(0);
  const pending = useRef(false);
  const cancel = () => {
    generation.current += 1;
    pending.current = false;
    setBusy(false);
  };
  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );

  const reset = () => {
    cancel();
    setPhone("");
    setCode("");
    setAttemptId(null);
    setBusy(false);
  };

  /** Back to the phone field, keeping what they typed. */
  const changeNumber = () => {
    cancel();
    setAttemptId(null);
    setCode("");
  };

  // A 10-digit US number; autofill's leading +1 is fine.
  const cleaned = digitsOnly(phone).replace(/^1(?=\d{10}$)/, "");
  const phoneValid = /^[2-9]\d{9}$/.test(cleaned);

  const sendCode = async (e: FormEvent) => {
    e.preventDefault();
    if (pending.current) return false;
    if (!phoneValid) {
      toast.error("Enter a valid 10-digit US phone number.");
      return false;
    }
    if (DEV_NO_SMS) {
      setAttemptId(-1);
      setCode("");
      toast.message("Dev: no text sent — any code works.");
      return true;
    }
    const request = ++generation.current;
    pending.current = true;
    setBusy(true);
    setCode("");
    try {
      await ensureSession();
      if (request !== generation.current) return false;
      const { data } = await apiClient.post<{
        result: { id: number };
      }>(API_ENDPOINTS.auth.getPhoneCode, { phone: cleaned });
      if (request !== generation.current) return false;
      const id = data?.result?.id;
      if (id == null) throw new Error("No attempt id");
      setAttemptId(id);
      if (showCodeSentToast) toast.success("Code sent.");
      return true;
    } catch {
      if (request !== generation.current) return false;
      toast.error("Couldn't send a code — check the number and try again.");
      return false;
    } finally {
      if (request === generation.current) {
        pending.current = false;
        setBusy(false);
      }
    }
  };

  /** A known operative: text the code to the number on file (the server
   *  never takes one from here), so they key in a code without typing their
   *  number. Resolves false when there is none, to fall back to asking. */
  const sendCodeToKnown = async () => {
    if (pending.current) return false;
    // (dev fakes it, except on one of our texted links: that knows who they
    // are, so it texts their real number and signs them in as themselves)
    if (DEV_NO_SMS && !opLink()) {
      setPhone("0000000000");
      setAttemptId(-1);
      setCode("");
      return true;
    }
    const request = ++generation.current;
    pending.current = true;
    setBusy(true);
    setCode("");
    try {
      await ensureSession();
      if (request !== generation.current) return false;
      const { data } = await apiClient.post<{
        result: { id: number; phone: string };
      }>(API_ENDPOINTS.auth.getPhoneCode, {
        known: true,
        op: opLink() || undefined,
      });
      if (request !== generation.current) return false;
      const id = data?.result?.id;
      if (id == null) throw new Error("No attempt id");
      setPhone(data.result.phone ?? "");
      setAttemptId(id);
      return true;
    } catch {
      return false;
    } finally {
      if (request === generation.current) {
        pending.current = false;
        setBusy(false);
      }
    }
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    if (pending.current) return;
    if (!attemptId || !code.trim()) {
      toast.error("Enter the code from your text.");
      return;
    }
    // only a faked attempt (dev) is waved through; a real one is checked
    if (attemptId === -1) {
      if (successMessage) toast.success(successMessage);
      // as a returning operative, so a dev sign-in reaches HQ
      onSignedIn?.({ memberId: 0, claimed: true });
      return;
    }
    const request = ++generation.current;
    pending.current = true;
    setBusy(true);
    try {
      const { data } = await apiClient.post<{ result?: SignedIn }>(
        API_ENDPOINTS.auth.verifyPhoneCode,
        {
          id: attemptId,
          code: code.trim(),
          phone: digitsOnly(phone),
          ...(signInOnly && { signInOnly: true }),
        },
      );
      // Persist a recovery sessionId the backend may return (stale-session
      // hotfix) BEFORE anything reads it — refreshChatSession and onSignedIn
      // both depend on the interceptor picking up the fresh value.
      const sid = data?.result?.sessionId;
      if (sid) localStorage.setItem("goodglows_sessionid", sid);
      void refreshChatSession(queryClient);
      if (request !== generation.current) return;
      if (successMessage) toast.success(successMessage);
      onSignedIn?.(data?.result);
    } catch {
      if (request !== generation.current) return;
      toast.error("That code didn't work — try again or resend.");
    } finally {
      if (request === generation.current) {
        pending.current = false;
        setBusy(false);
      }
    }
  };

  return {
    phone,
    setPhone,
    code,
    setCode,
    attemptId,
    busy,
    phoneValid,
    sendCode,
    sendCodeToKnown,
    verify,
    changeNumber,
    reset,
  };
}

const OP_KEY = "secret-missions-op";
/**
 * The signed `?op=` on a link we texted them: who the text went to. Kept past
 * the page it arrived on, so a browser that has not met them can still text a
 * code to their number (it never signs them in by itself).
 */
export const opLink = () => {
  try {
    const op = new URL(window.location.href).searchParams.get("op");
    if (op) localStorage.setItem(OP_KEY, op);
    return localStorage.getItem(OP_KEY) ?? "";
  } catch {
    return "";
  }
};

/**
 * Whether the session's member already has a verified number — a returning
 * operative files a debrief without the phone check. Reads it off the session endpoint, which
 * is idempotent for a visitor who already has a session.
 */
export const useHasPhone = (allowBeta = false) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["secretmissions", "session", "hasPhone", allowBeta],
    queryFn: async () => {
      await ensureSession();
      const { data } = await apiClient.post<{
        result: { hasPhone?: boolean; betaAccess?: boolean };
      }>(API_ENDPOINTS.session.ensure, {});
      return Boolean(
        data?.result?.hasPhone || (allowBeta && data?.result?.betaAccess),
      );
    },
    staleTime: 0,
  });
  useEffect(() => {
    if (
      query.data &&
      queryClient.getQueryState(sessionQueryOptions().queryKey)?.status ===
        "error"
    )
      void refreshChatSession(queryClient);
  }, [query.data, query.dataUpdatedAt, queryClient]);
  return query;
};
